export const HEFT_AUTHORITY_STATES = [
    'warranted',
    'reviewed',
    'accepted',
    'applied',
    'subject_confirmed',
] as const

export type HeftAuthorityState = (typeof HEFT_AUTHORITY_STATES)[number]

/**
 * Golden Spec domain vocabulary → human UI copy.
 *
 * `confirmed` is not a domain state. Live capture may still say "Confirm"
 * as conversational copy when the underlying state is `subject_confirmed`.
 */
export const HEFT_AUTHORITY_LANGUAGE = {
    reviewed: {
        status: 'Reviewed',
        pending: 'Needs review',
        action: 'Mark reviewed',
        undo: 'Mark needs review',
    },
    subject_confirmed: {
        status: 'Verified',
        pending: 'Not yet verified',
        action: 'Confirm',
        actionLong: 'Yes, that’s correct',
    },
    warranted: {
        status: 'Interpretation approved',
        pending: 'Needs interpretation review',
        action: 'Approve interpretation',
    },
    accepted: {
        status: 'Accepted',
        pending: 'Pending',
        action: 'Accept candidate',
        actionShort: 'Accept',
        actionBatch: 'Accept selected',
    },
    applied: {
        status: 'Added to HEFT',
        pending: 'Not applied',
        action: 'Apply',
        actionBatch: 'Apply accepted',
    },
} as const satisfies Record<
    HeftAuthorityState,
    {
        status: string
        pending: string
        action: string
        undo?: string
        actionLong?: string
        actionShort?: string
        actionBatch?: string
    }
>

export function heftAuthorityStatusLabel(state: HeftAuthorityState, standing: 'done' | 'pending' = 'done'): string {
    return standing === 'pending' ? HEFT_AUTHORITY_LANGUAGE[state].pending : HEFT_AUTHORITY_LANGUAGE[state].status
}

export function heftAuthorityActionLabel(
    state: HeftAuthorityState,
    variant: 'action' | 'actionShort' | 'actionLong' | 'actionBatch' | 'undo' = 'action'
): string {
    const labels = HEFT_AUTHORITY_LANGUAGE[state]
    if (variant === 'actionShort' && 'actionShort' in labels && labels.actionShort) return labels.actionShort
    if (variant === 'actionLong' && 'actionLong' in labels && labels.actionLong) return labels.actionLong
    if (variant === 'actionBatch' && 'actionBatch' in labels && labels.actionBatch) return labels.actionBatch
    if (variant === 'undo' && 'undo' in labels && labels.undo) return labels.undo
    return labels.action
}

/** Profile projection `user_confirmed` is review standing, not admission. */
export function heftReviewProjectionLabel(reviewed: boolean): string {
    return heftAuthorityStatusLabel('reviewed', reviewed ? 'done' : 'pending')
}

/**
 * Harvest maturity `confirmed` is live capture verification (`subject_confirmed`),
 * while `accepted_for_apply` is interviewer curation staged for profile apply.
 * Do not display either as generic Confirmed.
 */
export function heftHarvestMaturityLabel(
    maturity: string | undefined,
    origin?: { fromStory?: boolean; proposition?: boolean }
): string {
    const fromStory = origin?.fromStory === true
    if (maturity === 'accepted_for_apply') return heftAuthorityStatusLabel('accepted')
    if (fromStory) {
        if (maturity === 'confirmed') return `${heftAuthorityStatusLabel('subject_confirmed')} · from story`
        if (maturity === 'heard') return 'Heard · derived from story'
        return 'Suggested · from story'
    }
    if (maturity === 'confirmed') return heftAuthorityStatusLabel('subject_confirmed')
    if (maturity === 'heard') return 'Heard in interview'
    if (maturity === 'suggested') return 'Suggested'
    if (origin?.proposition) return 'Heard · proposition'
    return 'Unlinked harvest'
}
